import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typography } from '@/styles/typography';
import { Button } from '@/components/base/Button';
import { useIsMobile, useIsTablet } from '@/lib/hooks/useMediaQuery';
import type { EventsHeaderProps } from '@/types/events';

/**
 * Document icon component matching LucideIcon signature
 * Used for the User Guide button
 */
const DocumentIcon = forwardRef<SVGSVGElement, LucideProps>(
  ({ className, ...props }, ref) => (
    <svg
      ref={ref}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path d="M5 0H23V22H21V2H5V0Z" fill="currentColor" />
      <path fillRule="evenodd" clipRule="evenodd" d="M1 4H19V24H1V4ZM3 6V22H17V6H3Z" fill="currentColor" />
      <rect x="5" y="9" width="10" height="2" fill="currentColor" />
      <rect x="5" y="13" width="10" height="2" fill="currentColor" />
      <rect x="5" y="17" width="6" height="2" fill="currentColor" />
    </svg>
  )
);
DocumentIcon.displayName = 'DocumentIcon';

/**
 * EventsHeader - Page header for Events page
 *
 * Displays the page title "Healthy Aging Events Spotlight", subtitle,
 * "Return to home" navigation link, and "Open user guide" action button.
 *
 * Layout is responsive:
 * - Desktop: title/subtitle left, button aligned bottom-right
 * - Tablet/Mobile: title, subtitle, then button below (stacked)
 *
 * @example
 * ```tsx
 * import { EventsHeader } from '@/components';
 *
 * <EventsHeader
 *   content={eventsHeaderContent}
 *   onOpenUserGuide={() => console.log('Open user guide modal')}
 * />
 * ```
 */
export const EventsHeader = forwardRef<HTMLDivElement, EventsHeaderProps>(
  ({ content, onOpenUserGuide, className }, ref) => {
    const isMobile = useIsMobile();
    const isTablet = useIsTablet();
    const isTabletOrBelow = isMobile || isTablet;

    if (!content) return null;

    return (
      <div
        ref={ref}
        className={cn('w-full py-0', isMobile ? 'px-4' : 'px-10', className)}
        role="banner"
        aria-label="Page header"
      >
        <div
          className={cn(
            'w-full max-w-[1360px] mx-auto',
            'bg-muted',
            'p-6',
            'flex flex-col'
          )}
        >
          {/* Return home link */}
          <Link
            to={content.returnLink.href}
            className={cn(
              'inline-flex items-center gap-2 w-fit mb-2',
              'text-[var(--color-primary-brand)] hover:text-[var(--color-primary-brand-hover)]',
              typography.subtitle,
              'transition-colors duration-150',
              'motion-reduce:transition-none',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 rounded'
            )}
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            {content.returnLink.text}
          </Link>

          {/* Page title */}
          <h1
            className={cn(
              'text-[32px] leading-[40px] font-semibold m-0',
              "font-['Source_Sans_Pro']",
              'text-foreground'
            )}
          >
            {content.title}
          </h1>

          {/* Subtitle row with User Guide button - 4px gap from title */}
          <div
            className={cn(
              'mt-1',
              isTabletOrBelow ? 'flex flex-col gap-3 items-start' : 'relative'
            )}
          >
            <p
              className={cn(
                typography.body,
                'text-foreground m-0 p-0'
              )}
            >
              {content.subtitle}
            </p>

            <Button
              variant="secondary"
              size="default"
              content={{
                text: content.userGuideButtonLabel,
                icon: DocumentIcon,
                ariaLabel: content.userGuideButtonLabel,
              }}
              onClick={onOpenUserGuide}
              className={cn(
                isMobile ? 'w-full' : undefined,
                !isTabletOrBelow && 'absolute right-0 bottom-0'
              )}
            />
          </div>
        </div>
      </div>
    );
  }
);

EventsHeader.displayName = 'EventsHeader';
